import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  PersonOff as UnsubIcon,
  Mail as MailIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';
import { useNotification } from '../../contexts/NotificationContext';
import { COUNTRY_NAME_BY_CODE } from '../../constants/countries';
import { parseServerTs, dayjs, getCurrentTz } from '../../timezone';

/**
 * Operator panel — waitlist viewer + broadcast composer.
 *
 * Two responsibilities, one page:
 *   1) Show every waitlist signup with status / source / captcha-verified
 *      flag, plus quick-actions (delete, mark unsubscribed).
 *   2) Compose and send a branded broadcast to every active subscriber,
 *      with a built-in dry-run / preview so the operator can sanity-check
 *      before hitting send.
 *
 * The broadcast template HTML the user types here is wrapped in the
 * VerOps email chrome on the backend (header bar + footer) — see
 * EmailService.renderBrandedTemplate. The composer therefore only takes
 * the inner body markup; we render the same chrome in the preview iframe
 * so what you see is what every recipient gets.
 */
const WaitlistPage = () => {
  const notify = useNotification();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/v1.0/waitlist');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Permanently delete ${row.email}?`)) return;
    try {
      await apiClient.delete(`/api/v1.0/waitlist/${row.id}`);
      notify.success('Removed');
      fetchData();
    } catch (err) {
      notify.error('Delete failed');
    }
  };

  const handleUnsubscribe = async (row) => {
    try {
      await apiClient.post(`/api/v1.0/waitlist/${row.id}/unsubscribe`);
      notify.success(`${row.email} marked unsubscribed`);
      fetchData();
    } catch (err) {
      notify.error('Update failed');
    }
  };

  const subscribers = data?.subscribers || [];
  const stats = data || { total: 0, pending: 0, unsubscribed: 0, bounced: 0 };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Waitlist</Typography>
          <Typography variant="body2" color="text.secondary">
            Public landing-page signups. Captcha-gated when an operator captcha is configured.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setComposerOpen(true)}
            disabled={stats.pending === 0}
          >
            Send broadcast
          </Button>
        </Stack>
      </Stack>

      {/* Stat strip */}
      <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 3 }}>
        <StatCard label="Total signups" value={stats.total} />
        <StatCard label="Active" value={stats.pending} color="success.main" />
        <StatCard label="Unsubscribed" value={stats.unsubscribed} color="warning.main" />
        <StatCard label="Bounced" value={stats.bounced} color="error.main" />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Updates</TableCell>
                <TableCell>Why VerOps</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Captcha</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Last emailed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && subscribers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No signups yet — share the landing page to get the list started.
                  </TableCell>
                </TableRow>
              )}
              {!loading && subscribers.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                    {row.email}
                  </TableCell>
                  <TableCell>{row.name || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}</TableCell>
                  <TableCell>
                    {row.country ? (
                      <Tooltip title={COUNTRY_NAME_BY_CODE[row.country] || row.country}>
                        <Chip label={row.country} size="small" variant="outlined" />
                      </Tooltip>
                    ) : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                    {row.phone || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
                  </TableCell>
                  <TableCell>
                    {row.productUpdatesOptIn
                      ? <Tooltip title="Opted in to product updates"><CheckIcon fontSize="small" color="success" /></Tooltip>
                      : <Tooltip title="Waitlist only"><CancelIcon fontSize="small" color="disabled" /></Tooltip>}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    {row.interestReason ? (
                      <Tooltip
                        title={<Box sx={{ whiteSpace: 'pre-wrap', maxWidth: 360 }}>{row.interestReason}</Box>}
                        placement="top-start"
                        arrow
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            verticalAlign: 'bottom',
                            color: 'text.primary',
                            fontSize: 13,
                          }}
                        >
                          {row.interestReason}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      color={row.status === 'PENDING' ? 'success' : row.status === 'BOUNCED' ? 'error' : 'default'}
                      variant={row.status === 'PENDING' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {row.captchaVerified
                      ? <Tooltip title="Captcha verified at signup"><CheckIcon fontSize="small" color="success" /></Tooltip>
                      : <Tooltip title="Captcha disabled or skipped"><CancelIcon fontSize="small" color="disabled" /></Tooltip>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {row.subscribedAt ? dayjs(parseServerTs(row.subscribedAt)).tz(getCurrentTz()).format('MMM D, HH:mm') : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {row.lastEmailedAt ? dayjs(parseServerTs(row.lastEmailedAt)).tz(getCurrentTz()).format('MMM D, HH:mm') : <Box component="span" sx={{ color: 'text.disabled' }}>never</Box>}
                  </TableCell>
                  <TableCell align="right">
                    {row.status === 'PENDING' && (
                      <Tooltip title="Mark unsubscribed">
                        <IconButton size="small" onClick={() => handleUnsubscribe(row)}>
                          <UnsubIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <BroadcastComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        recipientCount={stats.pending}
        onSent={() => { setComposerOpen(false); fetchData(); }}
      />
    </Box>
  );
};

const StatCard = ({ label, value, color }) => (
  <Paper variant="outlined" sx={{ p: 1.5, minWidth: 140 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary', mt: 0.5 }}>
      {value ?? 0}
    </Typography>
  </Paper>
);

/**
 * Modal composer — subject, HTML body, dry-run toggle, preview.
 *
 * The body field accepts raw HTML. The preview iframe renders the body
 * inside the same VerOps chrome the backend wraps it in, so the
 * operator sees the final rendered email before sending.
 */
const BroadcastComposer = ({ open, onClose, recipientCount, onSent }) => {
  const notify = useNotification();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(SAMPLE_BODY);
  const [dryRun, setDryRun] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const previewHtml = useMemo(
    () => buildPreviewHtml(subject || 'Subject preview', body),
    [subject, body],
  );

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      notify.error('Subject and body are required');
      return;
    }
    if (dryRun && !testEmail.trim()) {
      notify.error('Provide a test email for the dry run');
      return;
    }
    if (!dryRun && !window.confirm(
      `Send to ${recipientCount} active subscriber${recipientCount === 1 ? '' : 's'}?`)) {
      return;
    }

    setSending(true);
    try {
      const res = await apiClient.post('/api/v1.0/waitlist/broadcast', {
        subject: subject.trim(),
        htmlBody: body,
        onlyActive: true,
        dryRun,
        testEmail: dryRun ? testEmail.trim() : null,
      });
      const r = res.data;
      if (r.success) {
        notify.success(
          dryRun
            ? `Test sent to ${testEmail}`
            : `Broadcast complete — ${r.recipientCount} recipient${r.recipientCount === 1 ? '' : 's'}, ${r.failedCount} failed`
        );
        if (!dryRun) onSent();
      } else {
        notify.error(r.message || 'Broadcast failed');
      }
    } catch (err) {
      notify.error(err.response?.data?.message || err.message || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MailIcon color="primary" />
          <Box>
            <Box sx={{ fontWeight: 600 }}>Compose broadcast</Box>
            <Typography variant="caption" color="text.secondary">
              {recipientCount} active recipient{recipientCount === 1 ? '' : 's'} on the waitlist · wrapped in VerOps email chrome
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="HTML body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={10}
            maxRows={20}
            size="small"
            sx={{ '& .MuiInputBase-input': { fontFamily: 'ui-monospace, monospace', fontSize: 13 } }}
            helperText="Inner body HTML. The platform wraps this in the VerOps header / footer automatically."
          />

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
              label="Dry-run (send to a single test address)"
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setShowPreview((v) => !v)}
            >
              {showPreview ? 'Hide preview' : 'Show preview'}
            </Button>
          </Stack>

          {dryRun && (
            <TextField
              label="Test email"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              fullWidth
              size="small"
              placeholder="you@verops.io"
            />
          )}

          {showPreview && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider', fontSize: 12, color: 'text.secondary' }}>
                Preview · this is what every recipient will see
              </Box>
              <iframe
                title="email preview"
                srcDoc={previewHtml}
                style={{ width: '100%', height: 480, border: 0, background: '#f6f8fa' }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={send}
          disabled={sending}
        >
          {dryRun ? 'Send test' : `Send to ${recipientCount}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SAMPLE_BODY = `<p>Hi there,</p>
<p>Thanks for joining the VerOps waitlist. We've just shipped a major update to the platform and wanted to share a few highlights.</p>
<ul>
  <li>Real-time Kubernetes object spec capture — full pod / service / event detail.</li>
  <li>Real CPU and memory metrics from the kubelet, no external collector required.</li>
  <li>Per-tenant retention controls, plan-aware time-range pickers across the dashboard.</li>
</ul>
<p>If you'd like an invite to the next round, just reply to this email.</p>
<p>— The VerOps team</p>`;

/** Mirrors EmailService.renderBrandedTemplate — keeps the preview
 *  pixel-equivalent to what the backend will actually send. If you
 *  change one, change the other. */
const buildPreviewHtml = (subject, innerBody) => `
<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${escapeHtml(subject)}</title></head>
<body style="margin:0;padding:0;background:#f6f8fa;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,Helvetica,Arial,sans-serif;color:#1f2328;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f6f8fa;">
  <tr><td align="center" style="padding:32px 16px;">
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#fff;border-radius:10px;overflow:hidden;box-shadow:0 1px 2px rgba(0,0,0,0.04);border:1px solid #d0d7de;">
      <tr><td style="padding:24px 32px;background:#0969da;color:#fff;">
        <table width="100%"><tr>
          <td style="font-size:20px;font-weight:700;letter-spacing:-0.01em;">VerOps</td>
          <td align="right" style="font-size:12px;opacity:0.85;text-transform:uppercase;letter-spacing:0.04em;">Observability</td>
        </tr></table>
      </td></tr>
      <tr><td style="padding:32px;font-size:15px;line-height:1.6;color:#1f2328;">${innerBody || ''}</td></tr>
      <tr><td style="padding:20px 32px;background:#f6f8fa;border-top:1px solid #d0d7de;font-size:12px;color:#57606a;">
        <p style="margin:0 0 6px 0;">You're receiving this because you joined the VerOps waitlist.</p>
        <p style="margin:0;">To stop receiving updates, reply with the word <strong>UNSUBSCRIBE</strong>.</p>
        <p style="margin:8px 0 0 0;color:#8b949e;">&copy; VerOps &middot; Observability that connects every layer of your stack.</p>
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;

const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

export default WaitlistPage;
